import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import AppsIcon from '@mui/icons-material/Apps';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PipelinesPage from './pipelinesPage';
import SummaryCard from './summaryCard';

import AnalyticsCard from './analyticsCard';
import LiveActivityFeed from './liveActivityFeed';

const Dashboard = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useUserContext();
  const apiContext = useApiContext();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [counts, setCounts] = useState({ apps: 0, sources: 0, agents: 0, pipelines: 0 });

  const fetchCounts = async () => {
    try {
      const countsData = await apiContext.getInstanceCounts();
      setCounts(countsData);
    } catch (err) {
      console.error('Failed to fetch counts', err);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchCounts();
  }, [user]);

  const buildApp = () => {
    navigate('/buildOptions');
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: { xs: 'auto', md: '100%' },
        p: 1.5,
        pt: 0.5,
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      <Grid container spacing={2} sx={{ height: { xs: 'auto', md: '100%' } }}>
        {/* Left Column - Main Content */}
        <Grid
          size={{ xs: 12, md: 8.5 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: { xs: 'auto', md: '100%' },
            gap: 1.5,
          }}
        >
          {/* Header for My Workspace */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5, mt: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 850, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '10px' }}>
              My Workspace
            </Typography>
            <Chip
              label="Private / Shared with me"
              size="small"
              sx={{
                height: 16,
                fontSize: '8px',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            />
          </Stack>

          {/* Hero / Summary Section */}
          <Grid container spacing={1.5} sx={{ width: '100%', m: 0 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <SummaryCard
                title="App Instances"
                count={counts.apps}
                icon={<AppsIcon />}
                active={false}
                color={theme.palette.secondary.main}
                onClick={() => navigate('/instances?tab=userAppInstances')}
                onAdd={() => navigate('/createUserAppInstance')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <SummaryCard
                title="Datasource Instances"
                count={counts.sources}
                icon={<StorageIcon />}
                active={false}
                color={theme.palette.secondary.main}
                onClick={() => navigate('/instances?tab=dataSourceInstances')}
                onAdd={() => navigate('/createDataSourceInstance')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <SummaryCard
                title="Agent Instances"
                count={counts.agents}
                icon={<SmartToyIcon />}
                active={false}
                color={theme.palette.secondary.main}
                onClick={() => navigate('/instances?tab=agentInstances')}
                onAdd={() => navigate('/createAgentInstance')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <SummaryCard
                title="Pipeline Instances"
                count={counts.pipelines}
                icon={<AccountTreeIcon />}
                active={true}
                color={theme.palette.secondary.main}
              />
            </Grid>
          </Grid>

          {/* Middle Section: Visual Pipelines Hero */}
          <Box
            sx={{
              flexGrow: 1,
              minHeight: { xs: 350, md: 0 },
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
              boxShadow: 'none',
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                px: 2.5,
                py: 1,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AccountTreeIcon sx={{ color: theme.palette.secondary.main, fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                Pipelines
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="secondary"
                onClick={buildApp}
                startIcon={<AddIcon sx={{ fontSize: '16px' }} />}
                size="small"
                sx={{
                  borderRadius: '10px',
                  px: 2,
                  py: 0.6,
                  fontWeight: 700,
                  fontSize: '12.5px',
                  color: 'text.primary',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                New Pipeline
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
              <PipelinesPage
                maxHeight={containerSize.height > 0 ? (isMobile ? 450 : containerSize.height - 150) : 450}
                onPipelineDeleted={fetchCounts}
              />
            </Box>
          </Box>
        </Grid>

        {/* Right Column - Controls & Widgets */}
        <Grid
          size={{ xs: 12, md: 3.5 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: { xs: 'auto', md: '100%' },
            gap: 1.5,
            overflowY: { xs: 'visible', md: 'auto' },
            pr: 0.5,
          }}
        >
          {/* Header for Tenant Activity */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5, mt: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 850, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '10px' }}>
              Tenant Activity
            </Typography>
            <Chip
              label="Tenant-wide"
              size="small"
              sx={{
                height: 16,
                fontSize: '8px',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                color: 'secondary.main',
              }}
            />
          </Stack>

          <AnalyticsCard />
          <LiveActivityFeed />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
